// NASA EONET v3 (JSON, CORS open): open natural events. Earthquakes are left to USGS.

import type { Fetcher } from '../types.ts';
import { getJson } from './http.ts';
import type { LiveKind, LiveRecord } from './types.ts';

const OPEN_EVENTS = 'https://eonet.gsfc.nasa.gov/api/v3/events?status=open&limit=100';
const PUBLISHER = 'NASA EONET';

const KIND: Record<string, LiveKind> = {
  severeStorms: 'cyclone', volcanoes: 'volcano', wildfires: 'wildfire', floods: 'flood', drought: 'drought',
};

interface EonetGeometry {
  date: string;
  type: string;
  coordinates: number[] | number[][][];
}

interface EonetEvent {
  id: string;
  title: string;
  link: string;
  categories: Array<{ id: string }>;
  geometry: EonetGeometry[];
}

/** First [lon, lat] of a Point or Polygon geometry. */
function firstPoint(g: EonetGeometry): [number, number] | null {
  const c = g.type === 'Polygon' ? (g.coordinates as number[][][])[0]?.[0] : (g.coordinates as number[]);
  return c && c.length >= 2 ? [c[0], c[1]] : null;
}

export async function eonetEvents(fetcher: Fetcher): Promise<LiveRecord[]> {
  const doc = await getJson<{ events: EonetEvent[] }>(fetcher, OPEN_EVENTS);
  const out: LiveRecord[] = [];

  for (const e of doc.events) {
    const kind = e.categories.map((c) => KIND[c.id]).find((k) => k !== undefined);
    const latest = e.geometry[e.geometry.length - 1];
    const at = latest ? firstPoint(latest) : null;
    if (!kind || !latest || !at) {
      continue;
    }
    out.push({
      id: `eonet:${e.id}`, kind, title: e.title, time: new Date(latest.date).toISOString(),
      lon: at[0], lat: at[1], url: e.link, publisher: PUBLISHER,
    });
  }
  return out;
}
